/**
 * Presentation-only mirror of the server's bank-staff / customer audience split.
 *
 * SOURCE OF TRUTH (backend): `libs/common-security/.../IdentityAudienceGuard.java` —
 * `isExclusiveBankStaffRoleNames()`. This file exists so SPA *chrome* (a top-bar toggle, a
 * staff-only affordance) can ask "is the signed-in identity a bank employee rather than a
 * customer-company user?" without a round trip.
 *
 * NOT AN AUTHORIZATION GATE. `Me.roles` is a display/audience array (ADR-0225 W4: the PDP ROLE
 * gate these used to feed is deleted); every mutation is still re-checked server-side, and any
 * capability decision must go through `EntitlementCapabilityService` / engine action codes. Use
 * this ONLY to decide whether a piece of chrome is worth showing — a wrong answer here must
 * cost a visitor nothing but a missing button.
 *
 * The rule is EXCLUSIVE, exactly like the server's: a customer-audience role (including
 * `demo-persona`) disqualifies the identity even when a bank-staff role name also appears, so
 * role overlap can never widen a demo persona into staff chrome.
 */

/** Roles that identify a customer-side audience (mirrors CUSTOMER_AUDIENCE_ROLES). */
const CUSTOMER_AUDIENCE_ROLES: ReadonlySet<string> = new Set([
  'customer',
  'demo-persona',
  'company-admin',
  'user',
  'customer-user',
  'vam-admin',
  'baas-program-admin',
]);

/** Roles that identify a bank employee rather than a customer-company user (mirrors BANK_STAFF_ROLES). */
const BANK_STAFF_ROLES: ReadonlySet<string> = new Set([
  'admin',
  'bank-admin',
  'ops',
  'back-office',
  'bank-ops-recovery',
  'billing-admin',
  'billing-approver',
  'billing-reader',
  'compliance-analyst',
  'compliance-mlro',
  'cooling-off-override',
  'credit-analyst',
  'customer-support',
  'onboarding-analyst',
  'platform-admin',
  'relationship-manager',
  'screening-analyst',
  'teller',
]);

/** `ROLE_Ops` / `ops` / ` OPS ` all normalize to `ops` — the server strips the prefix the same way. */
function normalizeRoleNames(roles: readonly string[] | null | undefined): string[] {
  return (roles ?? [])
    .filter((role): role is string => typeof role === 'string')
    .map((role) => role.trim().replace(/^ROLE_/i, '').toLowerCase())
    .filter((role) => role.length > 0);
}

/**
 * True only for an identity that carries a bank-employee role and NO customer-audience role.
 *
 * Fails closed: an empty/absent `roles` array is not staff.
 */
export function isExclusiveBankStaffRoles(roles: readonly string[] | null | undefined): boolean {
  const normalized = normalizeRoleNames(roles);
  if (normalized.some((role) => CUSTOMER_AUDIENCE_ROLES.has(role))) {
    return false;
  }
  return normalized.some((role) => BANK_STAFF_ROLES.has(role));
}
